/**
 * Runtime Configuration Service
 *
 * Fetches application configuration from the API at runtime.
 * This allows configuration to be managed server-side without rebuilding the frontend.
 */

import { logger } from './logger';

export interface RuntimeConfig {
  userPoolId: string;
  userPoolClientId: string;
  apiUrl: string;
  appSyncEventsEndpoint?: string;
  stage: string;
}

// Singleton to store the fetched config
let cachedConfig: RuntimeConfig | null = null;
let configPromise: Promise<RuntimeConfig> | null = null;

// Fallback to environment variables if API call fails
const fallbackConfig: RuntimeConfig = {
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
  userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
  apiUrl: process.env.REACT_APP_API_URL || '',
  appSyncEventsEndpoint: process.env.REACT_APP_APPSYNC_EVENTS_ENDPOINT || '',
  stage: process.env.NODE_ENV === 'production' ? 'prod' : 'dev',
};

/**
 * Fetch runtime configuration from the API
 * Uses a bootstrap URL that can be set via environment variable
 */
export async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing promise if fetch is in progress
  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    // Skip runtime config fetch if explicitly disabled (useful for local dev with proxy)
    if (process.env.REACT_APP_SKIP_RUNTIME_CONFIG === 'true') {
      logger.info('config', 'Runtime config fetch skipped, using environment variables');
      cachedConfig = fallbackConfig;
      return fallbackConfig;
    }

    // The bootstrap URL - either from env var or we construct it
    const configUrl = process.env.REACT_APP_CONFIG_URL ||
      (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/config/public` : null);

    if (!configUrl) {
      logger.warn('config', 'No config URL available, using fallback environment variables');
      cachedConfig = fallbackConfig;
      return fallbackConfig;
    }

    try {
      logger.info('config', 'Fetching runtime configuration', { configUrl });

      const response = await fetch(configUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      cachedConfig = {
        userPoolId: data.userPoolId || fallbackConfig.userPoolId,
        userPoolClientId: data.userPoolClientId || fallbackConfig.userPoolClientId,
        apiUrl: data.apiUrl || fallbackConfig.apiUrl,
        appSyncEventsEndpoint: data.appSyncEventsEndpoint || fallbackConfig.appSyncEventsEndpoint,
        stage: data.stage || fallbackConfig.stage,
      };

      logger.info('config', 'Runtime configuration loaded successfully', {
        hasUserPoolId: Boolean(cachedConfig.userPoolId),
        hasClientId: Boolean(cachedConfig.userPoolClientId),
        hasApiUrl: Boolean(cachedConfig.apiUrl),
        stage: cachedConfig.stage,
      });

      return cachedConfig;
    } catch (error) {
      logger.error('config', 'Failed to fetch runtime configuration, using fallback', error);
      console.warn('Failed to fetch runtime config, using environment variables:', error);
      cachedConfig = fallbackConfig;
      return fallbackConfig;
    }
  })();

  return configPromise;
}

/**
 * Get the current runtime configuration
 * Returns null if config hasn't been fetched yet
 */
export function getRuntimeConfig(): RuntimeConfig | null {
  return cachedConfig;
}

/**
 * Check if runtime config has been loaded
 */
export function isConfigLoaded(): boolean {
  return cachedConfig !== null;
}

/**
 * Clear the cached configuration (useful for testing)
 */
export function clearConfigCache(): void {
  cachedConfig = null;
  configPromise = null;
}

/**
 * Check if the application is running in a non-production environment
 * Returns true for 'dev', 'staging', 'development', or any stage that is not 'prod' or 'production'
 */
export function isNonProduction(): boolean {
  const stage = cachedConfig?.stage || fallbackConfig.stage;
  return !['prod', 'production'].includes(stage.toLowerCase());
}
